import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  ArrowUpRight, TrendingUp, TrendingDown, Building2, RefreshCw,
  ChevronRight, AlertCircle, DollarSign, CreditCard, ArrowRight,
  Users, FileText
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// ── Formatters ────────────────────────────────────────────────────────────────

const fmtNum = (n, decimals = 2) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n || 0);

const fmtMoney = (n, currency = 'USD') => {
  const abs = Math.abs(n || 0);
  const sign = n < 0 ? '-' : '';
  const sym = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
  if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${sign}${sym}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${sym}${fmtNum(abs)}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionTitle({ children }) {
  return (
    <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">
      {children}
    </h2>
  );
}

function Card({ children, className = '', noPad = false }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-lg ${noPad ? '' : 'p-5'} ${className}`}>
      {children}
    </div>
  );
}

function CardHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-slate-100">
      <div>
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ── Bank Account Card ─────────────────────────────────────────────────────────

function BankCard({ acct }) {
  const isCreditCard = acct.subtype === 'credit_card';
  const isNeg  = acct.balance < 0;
  const sym    = acct.currency === 'GBP' ? '£' : '$';
  // Credit cards: positive balance = money owed (liability)
  const displayAmt  = Math.abs(acct.balance);
  const labelColor  = isCreditCard
    ? (acct.balance > 0 ? 'text-amber-600' : 'text-emerald-600')
    : (isNeg ? 'text-red-600' : 'text-slate-900');
  const cardColor   = isCreditCard ? 'bg-amber-500' : 'bg-blue-600';
  const balanceLabel = isCreditCard
    ? (acct.balance > 0 ? 'Balance owed' : 'Credit balance')
    : 'Balance in Eyvo';

  return (
    <Card noPad data-testid={`bank-card-${acct.code}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded flex items-center justify-center ${cardColor}`}>
              <CreditCard size={13} className="text-white" />
            </div>
            <p className="text-xs font-semibold text-slate-800 leading-tight">{acct.name}</p>
          </div>
          <span className="text-xs text-slate-400 font-mono">{acct.currency}</span>
        </div>
        <p className={`text-xl font-bold font-mono mt-3 ${labelColor}`}>
          {sym}{fmtNum(displayAmt)}
          {!isCreditCard && isNeg && <span className="text-sm font-normal ml-1 text-red-400">CR</span>}
        </p>
        <p className="text-xs text-slate-400 mt-1">{balanceLabel}</p>
      </div>
      <div className="border-t border-slate-100 px-4 py-2">
        <Link
          to={`/accounts/usa/transactions?account=${encodeURIComponent(acct.name)}`}
          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
          data-testid={`bank-card-link-${acct.code}`}
        >
          View transactions <ChevronRight size={11} />
        </Link>
      </div>
    </Card>
  );
}

// ── AR Aging Bar chart ────────────────────────────────────────────────────────

function InvoicesOwedWidget({ ar, loading }) {
  if (loading) return (
    <Card noPad className="animate-pulse">
      <div className="p-5 space-y-3">
        {[...Array(4)].map((_, i) => <div key={i} className="h-4 bg-slate-100 rounded" />)}
      </div>
    </Card>
  );

  const { awaiting_count, overdue_count, overdue_amount, total_owed, ar_balance } = ar || {};
  const hasData = (ar_balance || 0) > 0 || (total_owed || 0) > 0;

  return (
    <Card noPad data-testid="invoices-owed-widget">
      <CardHeader
        title="Invoices owed to you"
        action={
          <Link to="/invoices" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
            View all <ChevronRight size={11} />
          </Link>
        }
      />
      <div className="p-5">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-2xl font-bold text-slate-900 font-mono">
              {fmtMoney(total_owed || ar_balance || 0)}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {awaiting_count || 0} awaiting payment
            </p>
          </div>
          {overdue_count > 0 && (
            <div>
              <p className="text-2xl font-bold text-red-600 font-mono">{fmtMoney(overdue_amount)}</p>
              <p className="text-xs text-red-400 mt-0.5">{overdue_count} overdue</p>
            </div>
          )}
        </div>

        {!hasData && (
          <div className="py-6 text-center">
            <p className="text-sm text-slate-400">No outstanding invoices</p>
            <p className="text-xs text-slate-300 mt-1">AR balance from Xero: {fmtMoney(ar_balance || 0)}</p>
          </div>
        )}
      </div>
      <div className="border-t border-slate-100 px-5 py-2.5 flex gap-4">
        <Link to="/invoices/new" className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
          New invoice <ArrowUpRight size={11} />
        </Link>
        <Link to="/invoices" className="text-xs text-slate-500 hover:text-slate-700 font-medium flex items-center gap-1">
          View all invoices <ChevronRight size={11} />
        </Link>
      </div>
    </Card>
  );
}

// ── Net P&L widget ────────────────────────────────────────────────────────────

function PLWidget({ pl, loading }) {
  if (loading) return (
    <Card noPad className="animate-pulse">
      <div className="p-5 space-y-3">
        {[...Array(3)].map((_, i) => <div key={i} className="h-4 bg-slate-100 rounded" />)}
      </div>
    </Card>
  );

  const chartData = pl ? [
    { name: 'Income',   value: Math.round(pl.income),   fill: '#1d4ed8' },
    { name: 'Expenses', value: Math.round(pl.expenses),  fill: '#93c5fd' },
  ] : [];

  return (
    <Card noPad data-testid="pl-widget">
      <CardHeader
        title="Net profit or loss"
        subtitle={pl ? `${fmtDate(pl.period_start)} – ${fmtDate(pl.period_end)} · Year to date` : 'Year to date'}
        action={
          <Link to="/accounts/usa/pl-report" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
            P&L Report <ChevronRight size={11} />
          </Link>
        }
      />
      <div className="p-5">
        <p className={`text-2xl font-bold font-mono ${(pl?.net_profit || 0) >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
          {fmtMoney(pl?.net_profit || 0)}
        </p>
        <div className="flex items-center gap-1 mt-0.5">
          {(pl?.net_profit || 0) >= 0
            ? <TrendingUp size={13} className="text-emerald-500" />
            : <TrendingDown size={13} className="text-red-500" />}
          <span className="text-xs text-slate-400">
            Income {fmtMoney(pl?.income || 0)} · Expenses {fmtMoney(pl?.expenses || 0)}
          </span>
        </div>

        {chartData.length > 0 && (
          <div className="mt-4 h-36">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barSize={48}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip
                  formatter={(v) => [fmtMoney(v), '']}
                  contentStyle={{ fontSize: 11, border: '1px solid #e2e8f0', borderRadius: 6 }}
                />
                <Bar dataKey="value" fill="#1d4ed8" radius={[3, 3, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <rect key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Cash in/out widget ────────────────────────────────────────────────────────

function CashFlowWidget({ cashFlow, loading }) {
  if (loading) return (
    <Card noPad className="animate-pulse">
      <div className="p-5 space-y-3">
        {[...Array(3)].map((_, i) => <div key={i} className="h-4 bg-slate-100 rounded" />)}
      </div>
    </Card>
  );

  const totalIn  = cashFlow.reduce((s, m) => s + m.cash_in, 0);
  const totalOut = cashFlow.reduce((s, m) => s + m.cash_out, 0);
  const diff     = totalIn - totalOut;

  return (
    <Card noPad data-testid="cashflow-widget">
      <CardHeader
        title="Cash in and out"
        subtitle="Last 6 months"
        action={
          <Link to="/accounts/usa/overview" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
            Bank Summary <ChevronRight size={11} />
          </Link>
        }
      />
      <div className="p-5">
        <div className="grid grid-cols-2 gap-4 mb-1">
          <div>
            <p className="text-xl font-bold font-mono text-slate-900">{fmtMoney(totalIn)}</p>
            <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-600 inline-block" /> Cash in
            </p>
          </div>
          <div>
            <p className="text-xl font-bold font-mono text-slate-900">{fmtMoney(totalOut)}</p>
            <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-300 inline-block" /> Cash out
            </p>
          </div>
        </div>
        <p className={`text-sm font-semibold font-mono mb-3 ${diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {diff >= 0 ? '+' : ''}{fmtMoney(diff)} difference
        </p>

        {cashFlow.length > 0 && (
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cashFlow} barGap={2} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                  tickFormatter={v => v.split(' ')[0]} />
                <YAxis hide />
                <Tooltip
                  formatter={(v, name) => [fmtMoney(v), name === 'cash_in' ? 'Cash in' : 'Cash out']}
                  contentStyle={{ fontSize: 11, border: '1px solid #e2e8f0', borderRadius: 6 }}
                />
                <Legend
                  formatter={v => v === 'cash_in' ? 'Cash in' : 'Cash out'}
                  iconType="circle" iconSize={8}
                  wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
                />
                <Bar dataKey="cash_in"  name="cash_in"  fill="#1d4ed8" radius={[2, 2, 0, 0]} />
                <Bar dataKey="cash_out" name="cash_out" fill="#93c5fd" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Recent Payments widget ────────────────────────────────────────────────────

function RecentPaymentsWidget({ payments, loading }) {
  return (
    <Card noPad data-testid="recent-payments-widget">
      <CardHeader
        title="Recent invoice payments"
        action={
          <Link to="/accounts/usa/transactions" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
            View all <ChevronRight size={11} />
          </Link>
        }
      />
      {loading ? (
        <div className="p-5 space-y-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}
        </div>
      ) : payments.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-sm text-slate-400">No recent payments found</p>
          <p className="text-xs text-slate-300 mt-1">AR credits will appear here</p>
        </div>
      ) : (
        <>
          <div className="px-5 pt-3 pb-1 grid grid-cols-[1fr_auto_auto] gap-x-4">
            <span className="text-xs text-slate-400 font-medium">Contact</span>
            <span className="text-xs text-slate-400 font-medium">Date</span>
            <span className="text-xs text-slate-400 font-medium text-right">Amount</span>
          </div>
          <div className="divide-y divide-slate-50">
            {payments.map((p, i) => (
              <div key={i} className="px-5 py-2.5 grid grid-cols-[1fr_auto_auto] gap-x-4 items-center hover:bg-slate-50">
                <Link
                  to={`/accounts/usa/transactions?q=${encodeURIComponent(p.contact)}`}
                  className="text-xs text-blue-600 hover:underline truncate"
                  data-testid={`payment-row-${i}`}
                >
                  {p.contact}
                </Link>
                <span className="text-xs text-slate-500 whitespace-nowrap">
                  {new Date(p.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </span>
                <span className="text-xs font-semibold text-slate-800 text-right font-mono whitespace-nowrap">
                  {fmtMoney(p.amount)}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 px-5 py-2.5">
            <Link to="/accounts/usa/transactions" className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
              View paid invoices <ChevronRight size={11} />
            </Link>
          </div>
        </>
      )}
    </Card>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  // The overview adds up both companies' cash, receivables and profit, so it
  // is for people who can see both. The API refuses everyone else; asking
  // anyway would only draw a screen of zeros, which reads as "nothing owed".
  const financial = hasPermission('accounts_usa') && hasPermission('accounts_india');
  const [summary, setSummary]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [contactCount, setContactCount] = useState(null);
  const [clientCount, setClientCount]   = useState(null);

  const load = useCallback(() => {
    if (!financial) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get('/api/dashboard/summary')
      .then(r => {
        setSummary(r.data);
        setLastRefresh(new Date());
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [financial]);

  useEffect(() => { load(); }, [load]);

  // The dashboard permission shows the dashboard, not every module's data:
  // each count is asked for only by someone holding that module.
  const seesSuppliers = hasPermission('suppliers');
  const seesClients = hasPermission('clients');
  useEffect(() => {
    if (seesSuppliers) {
      api.get('/api/suppliers?limit=1')
        .then(r => setContactCount(r.data.total))
        .catch(() => setContactCount(0));
    }
    if (seesClients) {
      api.get('/api/clients')
        .then(r => setClientCount(r.data.total))
        .catch(() => setClientCount(0));
    }
  }, [seesSuppliers, seesClients]);

  const greet = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const bank   = summary?.bank_accounts    || [];
  const ar     = summary?.ar_summary       || {};
  const pl     = summary?.pl_ytd           || null;
  const cash   = summary?.cash_flow_6m     || [];
  const pmts   = summary?.recent_payments  || [];

  const totalBankUSD = bank.filter(b => b.currency === 'USD').reduce((s, b) => s + b.balance, 0);
  const totalBankGBP = bank.filter(b => b.currency === 'GBP').reduce((s, b) => s + b.balance, 0);

  return (
    <div className="p-6 max-w-screen-xl" data-testid="dashboard-page" style={{ backgroundColor: '#f8fafc', minHeight: '100%' }}>

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Business Overview
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            {greet()}{user?.first_name ? `, ${user.first_name}` : ''} — {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        {financial && <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 border border-slate-200 rounded-md hover:bg-white hover:border-slate-300 bg-white"
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          {lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'Refresh'}
        </button>}
      </div>

      {!financial && (
        <div data-testid="dashboard-restricted"><Card>
          <p className="text-sm font-semibold text-slate-800">Financial overview not available</p>
          <p className="text-xs text-slate-500 mt-1 mb-4">
            The overview combines Eyvo USA and Eyvo India, and your account does not have access to both.
            Use the sections in the menu that you have been given.
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              seesSuppliers && { label: 'Suppliers', to: '/suppliers', icon: Users },
              seesClients   && { label: 'Clients',   to: '/clients',   icon: Building2 },
            ].filter(Boolean).map(a => (
              <Link key={a.to} to={a.to}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 text-xs font-medium text-slate-700 hover:bg-blue-50 hover:border-blue-200">
                <a.icon size={14} className="text-slate-400" /> {a.label}
              </Link>
            ))}
          </div>
        </Card></div>
      )}

      {financial && <>
      {/* ── Summary ribbon ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total USD Cash', value: fmtMoney(totalBankUSD, 'USD'), sub: 'Combined USD accounts', icon: DollarSign, color: 'text-blue-700 bg-blue-50', to: '/accounts/usa/transactions' },
          { label: 'Total GBP Cash', value: `£${fmtNum(Math.abs(totalBankGBP))}`, sub: 'Combined GBP accounts', icon: Building2, color: 'text-indigo-700 bg-indigo-50', to: '/accounts/usa/transactions' },
          { label: 'Net Profit YTD', value: fmtMoney(pl?.net_profit || 0), sub: 'Jan 1 to today', icon: TrendingUp, color: (pl?.net_profit || 0) >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50', to: '/accounts/usa/pl-report' },
          seesSuppliers && { label: 'Suppliers', value: contactCount !== null ? contactCount.toLocaleString() : '—', sub: 'Local master data', icon: Users, color: 'text-violet-700 bg-violet-50', to: '/suppliers' },
        ].filter(Boolean).map(s => (
          <Link key={s.label} to={s.to}
            className="bg-white border border-slate-200 rounded-lg px-4 py-3 hover:shadow-sm flex items-center gap-3"
            data-testid={`summary-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${s.color}`}>
              <s.icon size={15} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-400 truncate">{s.label}</p>
              <p className="text-sm font-bold text-slate-900 font-mono">{loading ? '—' : s.value}</p>
              <p className="text-xs text-slate-400 truncate">{s.sub}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* ── Bank Account Cards ── */}
      <SectionTitle>Bank Accounts</SectionTitle>
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-6">
        {loading
          ? [...Array(4)].map((_, i) => <div key={i} className="h-32 bg-white border border-slate-200 rounded-lg animate-pulse" />)
          : bank.map(b => <BankCard key={b.code} acct={b} />)
        }
      </div>

      {/* ── Row 2: Invoices + P&L + Payments ── */}
      <SectionTitle>Financial Position</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <InvoicesOwedWidget ar={ar} loading={loading} />
        <PLWidget pl={pl} loading={loading} />
        <RecentPaymentsWidget payments={pmts} loading={loading} />
      </div>

      {/* ── Row 3: Cash Flow ── */}
      <SectionTitle>Cash Flow · Last 6 Months</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <CashFlowWidget cashFlow={cash} loading={loading} />

        {/* Quick Links */}
        <Card data-testid="quick-links-card">
          <p className="text-sm font-semibold text-slate-800 mb-4">Quick Actions</p>
          <div className="space-y-2">
            {[
              { label: 'View P&L Report',        to: '/accounts/usa/pl-report',    icon: TrendingUp  },
              { label: 'View Balance Sheet',      to: '/accounts/usa/balance-sheet', icon: Building2   },
              { label: 'All Transactions',        to: '/accounts/usa/transactions',  icon: ArrowRight  },
              { label: 'Journal Entries',         to: '/accounts/usa/journal-entries', icon: FileText  },
              { label: 'Xero Import Status',      to: '/accounts/usa/xero-import',   icon: RefreshCw  },
              seesSuppliers && { label: `Suppliers (${contactCount !== null ? contactCount.toLocaleString() : '…'})`, to: '/suppliers', icon: Users },
              seesClients   && { label: `Clients (${clientCount !== null ? clientCount.toLocaleString() : '…'}) · Pulse`, to: '/clients', icon: Building2 },
            ].filter(Boolean).map(a => (
              <Link key={a.to} to={a.to}
                className="flex items-center justify-between p-2.5 rounded-md border border-slate-100 hover:bg-blue-50 hover:border-blue-200 group transition-colors"
                data-testid={`quicklink-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-2.5">
                  <a.icon size={14} className="text-slate-400 group-hover:text-blue-600" />
                  <span className="text-xs font-medium text-slate-700 group-hover:text-blue-800">{a.label}</span>
                </div>
                <ChevronRight size={12} className="text-slate-300 group-hover:text-blue-500" />
              </Link>
            ))}
          </div>
        </Card>
      </div>
      </>}

    </div>
  );
}
